import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { useHeaderNavOverlap } from '../hooks/useHeaderNavOverlap'
import LanguageSwitcher from './LanguageSwitcher'
import { CLIENT_PORTAL_URL } from '../constants/external-links'
import { Button } from '@/components/ui/button'
import {
  IconSlidingScale,
  IconEmdr,
  MODALITY_ICON_MENU_CLASS
} from './ModalityServiceIcons.jsx'

const ExternalLinkIcon = ({ className = 'h-3.5 w-3.5 shrink-0 opacity-60' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
)

const HeaderSinglePage = () => {
  const t = useTranslation()
  const { isLoading, language } = useLanguage()
  const location = useLocation()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [openDesktopMegaId, setOpenDesktopMegaId] = useState(null)
  const [openMobileMegaId, setOpenMobileMegaId] = useState(null)
  const megaRefs = useRef({})
  /** After choosing a child link, block hover reopen until pointer leaves the mega trigger. */
  const megaHoverLockRef = useRef({})
  const [activeSection, setActiveSection] = useState('')

  // Hash-based nav only works on the home single-page routes (/, /en, /pt).
  const isHomeSinglePage = ['/', '/en', '/pt'].includes(location.pathname)
  const isOnSeparatePage = !isHomeSinglePage
  const isTeamRoutePath = /^\/(en\/|pt\/)?team(\/.*)?$/.test(location.pathname)

  const marketingHomePath = language === 'pt' ? '/pt' : language === 'en' ? '/en' : '/'

  const navigateToHomeSection = (hashHref) => {
    const hash = hashHref.startsWith('#') ? hashHref : `#${hashHref}`
    navigate(`${marketingHomePath}${hash}`)
  }

  const hrefLang = (path) => {
    const p = path.startsWith('/') ? path : `/${path}`
    if (language === 'pt') return `/pt${p}`
    if (language === 'en') return `/en${p}`
    return p
  }

  /** True on EMDR, modalities-rates, sliding-scale (any lang prefix) */
  const isEmdrPage = /^\/(en\/|pt\/)?emdr\/?$/.test(location.pathname)
  const isModalitiesRatesPage = /^\/(en\/|pt\/)?modalities-rates\/?$/.test(location.pathname)
  const isSlidingScalePage = /^\/(en\/|pt\/)?sliding-scale\/?$/.test(location.pathname)
  const isGroupsPage = /^\/(en\/|pt\/)?groups(\/[^/]+)?\/?$/.test(location.pathname)
  const isServicesInvestmentPage = /^\/(en\/|pt\/)?services-investment\/?$/.test(location.pathname)
  const isServicesRelatedPage =
    isEmdrPage || isModalitiesRatesPage || isSlidingScalePage || isGroupsPage || isServicesInvestmentPage

  const navigation = useMemo(
    () => [
      {
        kind: 'mega',
        megaId: 'services',
        key: 'common.nav.emdr',
        sectionId: 'emdr',
        icon: 'emdr',
        contentKey: 'servicesMega',
        linkType: 'route',
        megaColumns: [
          [
            { to: hrefLang('/emdr'), itemId: 'emdr' },
            { to: hrefLang('/sliding-scale'), itemId: 'slidingScale' },
            { to: hrefLang('/groups'), itemId: 'groups' },
            { to: hrefLang('/services-investment'), itemId: 'servicesInvestment' }
          ]
        ]
      },
      {
        kind: 'mega',
        megaId: 'getToKnowViva',
        key: 'common.nav.getToKnowViva',
        icon: 'users',
        contentKey: 'getToKnowVivaMega',
        linkType: 'section',
        activeSectionIds: ['team', 'conditions-we-treat', 'our-office'],
        megaColumns: [
          [
            { itemId: 'team', href: '#team', sectionId: 'team' },
            { itemId: 'conditions', href: '#conditions-we-treat', sectionId: 'conditions-we-treat' },
            { itemId: 'office', href: '#our-office', sectionId: 'our-office' },
            { to: hrefLang('/locations'), itemId: 'locations' }
          ]
        ]
      },
      { kind: 'link', key: 'common.nav.faq', href: '#faq', sectionId: 'faq', label: 'FAQ', icon: 'question' },
      {
        kind: 'link',
        key: 'common.nav.intakeForms',
        href: hrefLang('/intake'),
        label: 'Intake Forms',
        icon: 'clipboard',
        isRoute: true,
      },
      {
        kind: 'link',
        key: 'common.nav.contact',
        href: hrefLang('/contact'),
        label: 'Contact Us',
        icon: 'envelope',
        isRoute: true,
      },
      {
        kind: 'external',
        key: 'common.nav.clientPortal',
        href: CLIENT_PORTAL_URL,
        icon: 'portal'
      }
    ],
    [language]
  )

  const {
    logoRef,
    navRef,
    langRef,
    navOverlapsLogo,
    showDesktopNav,
  } = useHeaderNavOverlap([language, navigation, isLoading])

  // Icon component helper
  const getIcon = (iconName) => {
    const iconClass = "w-6 h-6"
    switch (iconName) {
      case 'emdr':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 12h3l2-4 3 8 3-8 2 4h3M9 12h.01M15 12h.01"
            />
          </svg>
        )
      case 'users':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        )
      case 'heart':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        )
      case 'building':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        )
      case 'map':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )
      case 'question':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'envelope':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      case 'clipboard':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        )
      case 'portal':
        return (
          <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )
      default:
        return null
    }
  }

  const renderMegaModalityIcon = (itemId) => {
    const mc = MODALITY_ICON_MENU_CLASS
    switch (itemId) {
      case 'emdr':
        return <IconEmdr className={mc} variant="line" />
      case 'servicesInvestment':
        return (
          <svg viewBox="0 0 80 80" className={mc} aria-hidden>
            <g
              fill="none"
              stroke="currentColor"
              strokeWidth={1.65}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="22" y="16" width="36" height="48" rx="3" />
              <line x1="30" y1="30" x2="50" y2="30" />
              <line x1="30" y1="40" x2="50" y2="40" />
              <line x1="30" y1="50" x2="44" y2="50" />
            </g>
          </svg>
        )
      case 'slidingScale':
        return <IconSlidingScale className={mc} variant="line" />
      case 'groups':
        return (
          <svg viewBox="0 0 80 80" className={mc} aria-hidden>
            <g
              fill="none"
              stroke="currentColor"
              strokeWidth={1.65}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="28" cy="28" r="10" />
              <circle cx="52" cy="28" r="10" />
              <circle cx="40" cy="50" r="10" />
              <path d="M18 62c2-8 8-12 14-12M48 38c6 0 12 4 14 12M34 60c2-6 6-9 12-9" />
            </g>
          </svg>
        )
      default:
        return null
    }
  }

  const getToKnowMegaIcon = (itemId) => {
    const map = { team: 'users', conditions: 'heart', office: 'building', locations: 'map' }
    return getIcon(map[itemId] || 'users')
  }

  const isMegaNavActive = (item) => {
    if (item.megaId === 'services') {
      return isOnSeparatePage ? isServicesRelatedPage : activeSection === item.sectionId
    }
    if (item.megaId === 'getToKnowViva') {
      return isOnSeparatePage
        ? isTeamRoutePath
        : (item.activeSectionIds || []).includes(activeSection)
    }
    return false
  }

  const closeAllMegas = () => {
    megaHoverLockRef.current = { services: true, getToKnowViva: true }
    setIsMenuOpen(false)
    setOpenMobileMegaId(null)
    setOpenDesktopMegaId(null)
  }

  const handleMegaMenuLinkClick = () => {
    window.setTimeout(closeAllMegas, 0)
  }

  const renderMegaMenuItem = (link, megaItem, isMobile) => {
    const itemClass = isMobile
      ? 'flex items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-white active:bg-white'
      : 'flex items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-primary-offWhite focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary-darkGreen/40'

    const title = t(`common.nav.${megaItem.contentKey}.items.${link.itemId}.title`)
    const desc = t(`common.nav.${megaItem.contentKey}.items.${link.itemId}.desc`)
    const iconEl =
      megaItem.linkType === 'route' ? (
        renderMegaModalityIcon(link.itemId)
      ) : (
        <span className="text-gray-500">{getToKnowMegaIcon(link.itemId)}</span>
      )

    const content = (
      <>
        <div className="flex w-11 shrink-0 justify-center pt-0.5">{iconEl}</div>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-bold text-dark-800">{title}</span>
          <span className="mt-0.5 block text-sm leading-snug text-gray-500">{desc}</span>
        </span>
      </>
    )

    if (link.to) {
      return (
        <li key={link.itemId}>
          <Link to={link.to} onClick={handleMegaMenuLinkClick} className={itemClass}>
            {content}
          </Link>
        </li>
      )
    }

    const sectionHref = `${marketingHomePath}${link.href}`

    if (isOnSeparatePage) {
      return (
        <li key={link.itemId}>
          <a
            href={sectionHref}
            onClick={(e) => {
              e.preventDefault()
              navigateToHomeSection(link.href)
              handleMegaMenuLinkClick()
            }}
            className={itemClass}
          >
            {content}
          </a>
        </li>
      )
    }

    return (
      <li key={link.itemId}>
        <a
          href={link.href}
          onClick={(e) => {
            scrollToSection(e, link.href)
            handleMegaMenuLinkClick()
          }}
          className={itemClass}
        >
          {content}
        </a>
      </li>
    )
  }

  const renderDesktopMega = (item) => {
    const active = isMegaNavActive(item)
    const isOpen = openDesktopMegaId === item.megaId

    return (
      <div
        key={item.key}
        ref={(el) => {
          megaRefs.current[item.megaId] = el
        }}
        className="group relative"
        onMouseEnter={() => {
          if (!megaHoverLockRef.current[item.megaId]) setOpenDesktopMegaId(item.megaId)
        }}
        onMouseLeave={() => {
          megaHoverLockRef.current[item.megaId] = false
          setOpenDesktopMegaId((current) => (current === item.megaId ? null : current))
        }}
      >
        <button
          type="button"
          className={`relative flex items-center gap-0.5 whitespace-nowrap px-2.5 py-2.5 text-base font-semibold transition-colors duration-200 ${
            active ? 'text-primary-darkGreen' : 'text-gray-700 hover:text-primary-darkGreen'
          }`}
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          {t(item.key)}
          <svg className="h-4 w-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          <span
            className={`absolute bottom-0 left-0 h-0.5 bg-primary-darkGreen transition-all duration-300 ${
              active ? 'w-full' : 'w-0 group-hover:w-full'
            }`}
          />
        </button>
        <div
          className={`absolute left-0 top-full z-50 w-[min(100vw-2rem,40rem)] origin-top pt-3 transition-[opacity,transform,visibility] duration-200 ease-out ${
            isOpen
              ? 'pointer-events-auto visible scale-100 opacity-100'
              : 'pointer-events-none invisible scale-95 opacity-0'
          }`}
        >
          <div className="rounded-xl border border-gray-200 bg-white py-4 pl-4 pr-5 shadow-xl ring-1 ring-black/5">
            <p className="mb-3 text-xs leading-snug text-dark-800">
              {t(`common.nav.${item.contentKey}.intro`)}
            </p>
            <ul className="min-w-0 space-y-0.5">
              {item.megaColumns.flat().map((link) => renderMegaMenuItem(link, item, false))}
            </ul>
          </div>
        </div>
      </div>
    )
  }

  const renderMobileMega = (item) => {
    const active = isMegaNavActive(item)
    const isOpen = openMobileMegaId === item.megaId

    return (
      <div key={item.key} className="overflow-hidden rounded-xl border border-gray-200">
        <button
          type="button"
          onClick={() => setOpenMobileMegaId((current) => (current === item.megaId ? null : item.megaId))}
          className={`flex w-full touch-manipulation items-center gap-4 px-4 py-4 text-left text-base font-medium transition-colors duration-200 ${
            active
              ? 'bg-primary-darkGreen/10 font-semibold text-primary-darkGreen'
              : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
          }`}
          aria-expanded={isOpen}
        >
          <span className={`shrink-0 ${active ? 'text-primary-darkGreen' : 'text-gray-600'}`}>
            {getIcon(item.icon)}
          </span>
          <span className="flex-1">{t(item.key)}</span>
          <svg
            className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen ? (
          <div className="space-y-1 border-t border-gray-100 bg-gray-50/90 px-2 py-2">
            <p className="px-2 pb-2 text-xs text-dark-800">{t(`common.nav.${item.contentKey}.intro`)}</p>
            <ul className="space-y-0.5">
              {item.megaColumns.flat().map((link) => renderMegaMenuItem(link, item, true))}
            </ul>
          </div>
        ) : null}
      </div>
    )
  }

  // Smooth scroll to section
  const scrollToSection = (e, href) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Extract target ID from href (handle both '#section' and 'section' formats)
    const targetId = href.replace(/^#+/, '')
    
    if (!targetId) {
      setIsMenuOpen(false)
      return
    }
    
    // Wait a bit for any DOM updates, then find the element
    setTimeout(() => {
      const element = document.getElementById(targetId)
      
      if (element) {
        const headerEl = document.querySelector('header')
        const headerHeight = headerEl?.offsetHeight ?? 56
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
        const offsetPosition = elementPosition - headerHeight - 8

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        })
      } else {
        // Fallback: try using the href directly as an anchor
        window.location.hash = targetId
      }
    }, 50)
    
    // Close mobile menu
    setIsMenuOpen(false)
  }

  useEffect(() => {
    if (!isMenuOpen) setOpenMobileMegaId(null)
  }, [isMenuOpen])

  const prevPathnameRef = useRef(location.pathname)

  // Close menus after route changes (not on initial mount).
  useEffect(() => {
    if (prevPathnameRef.current === location.pathname) return
    prevPathnameRef.current = location.pathname
    setIsMenuOpen(false)
    setOpenMobileMegaId(null)
    megaHoverLockRef.current = { services: true, getToKnowViva: true }
    setOpenDesktopMegaId(null)
  }, [location.pathname])

  // Close desktop mega panel when clicking outside.
  useEffect(() => {
    if (!openDesktopMegaId) return
    const onPointerDown = (e) => {
      const ref = megaRefs.current[openDesktopMegaId]
      if (ref?.contains(e.target)) return
      setOpenDesktopMegaId(null)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [openDesktopMegaId])

  useBodyScrollLock(isMenuOpen)

  useEffect(() => {
    if (!isMenuOpen) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false)
        setOpenMobileMegaId(null)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isMenuOpen])

  // Detect active section based on scroll position (only on home page)
  useEffect(() => {
    if (isOnSeparatePage) return // Don't track scroll on separate pages
    
    const handleScroll = () => {
      const sections = navigation
        .flatMap((item) => {
          if (item.kind === 'mega' && item.linkType === 'section') {
            return item.megaColumns.flat().map((link) => ({
              id: link.sectionId,
              element: document.getElementById(link.sectionId)
            }))
          }
          if (item.sectionId) {
            return [{ id: item.sectionId, element: document.getElementById(item.sectionId) }]
          }
          return []
        })
        .filter((section) => section.element !== null)

      const scrollPosition = window.scrollY + 100 // Offset for header

      // Find the current section
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section.element) {
          const offsetTop = section.element.offsetTop
          if (scrollPosition >= offsetTop) {
            setActiveSection(section.id)
            break
          }
        }
      }

      // Check if we're at the top of the page (no in-nav section highlighted)
      if (window.scrollY < 100) {
        setActiveSection('')
      }
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Initial check

    return () => window.removeEventListener('scroll', handleScroll)
  }, [isOnSeparatePage, navigation])

  // Show loading state if translations are still loading
  if (isLoading) {
    return (
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200 pt-[env(safe-area-inset-top,0px)]">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center min-h-14">
            <div className="flex-shrink-0">
              <Link to={language === 'pt' ? '/pt' : language === 'en' ? '/en' : '/'} className="block">
                <h1 className="text-lg font-bold text-primary-darkGreen hover:text-primary-darkGreen/80 transition-colors duration-200 cursor-pointer">
                  {t('common.brand.name')}
                </h1>
              </Link>
            </div>
            <div className="animate-pulse bg-gray-200 h-6 w-20 rounded"></div>
          </div>
        </div>
      </header>
    )
  }

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200 transition-all duration-300 pt-[env(safe-area-inset-top,0px)]">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center min-h-14 w-full gap-2 lg:gap-3 xl:gap-4">
          {/* Logo — natural width only */}
          <div ref={logoRef} className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className={`${showDesktopNav ? 'lg:hidden' : ''} inline-flex items-center justify-center p-3 rounded-full text-gray-700 hover:text-primary-darkGreen hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-darkGreen touch-manipulation`}
              aria-controls="mobile-menu"
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className="sr-only">{t('common.buttons.openMenu', 'Open main menu')}</span>
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-7 w-7`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-7 w-7`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {isOnSeparatePage ? (
              <Link
                to={marketingHomePath}
                className="block shrink-0"
                aria-label={t('common.brand.name')}
              >
                <span className="cursor-pointer whitespace-nowrap text-xl font-bold text-primary-darkGreen transition-colors duration-200 hover:text-primary-darkGreen/80 md:text-2xl">
                  {t('common.brand.name')}
                </span>
              </Link>
            ) : (
              <a
                href="#home"
                className="block shrink-0"
                aria-label={t('common.brand.name')}
                onClick={(e) => scrollToSection(e, '#home')}
              >
                <span className="cursor-pointer whitespace-nowrap text-xl font-bold text-primary-darkGreen transition-colors duration-200 hover:text-primary-darkGreen/80 md:text-2xl">
                  {t('common.brand.name')}
                </span>
              </a>
            )}
          </div>

          {/* Nav — uses all space between logo and language; items centered in that corridor */}
          <nav
            className={`hidden min-w-0 flex-1 items-center justify-center ${showDesktopNav ? 'lg:flex' : ''}`}
            aria-label="Main"
          >
            <div
              ref={navRef}
              className="flex shrink-0 items-center gap-x-3 xl:gap-x-4 2xl:gap-x-5"
            >
            {navigation.map((item) => {
              if (item.kind === 'external') {
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative inline-flex items-center gap-1 whitespace-nowrap px-2.5 py-2.5 text-base font-semibold text-gray-700 transition-colors duration-200 hover:text-primary-darkGreen"
                  >
                    {t(item.key)}
                    <ExternalLinkIcon />
                    <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-primary-darkGreen transition-all duration-300 group-hover:w-full" />
                  </a>
                )
              }

              if (item.kind === 'mega') {
                return renderDesktopMega(item)
              }

              const isTeamItem = item.sectionId === 'team'
              const active = isOnSeparatePage
                ? isTeamRoutePath && isTeamItem
                : activeSection === item.sectionId

              if (item.isRoute) {
                const to = item.href.startsWith('/') ? item.href : `/${item.href}`
                const routeActive = location.pathname === to || location.pathname.endsWith(to)
                return (
                  <Link
                    key={item.key}
                    to={to}
                    className={`group relative whitespace-nowrap px-2.5 py-2.5 text-base font-semibold transition-colors duration-200 ${
                      routeActive ? 'text-primary-darkGreen' : 'text-gray-700 hover:text-primary-darkGreen'
                    }`}
                  >
                    {t(item.key)}
                    <span
                      className={`absolute bottom-0 left-0 h-0.5 bg-primary-darkGreen transition-all duration-300 ${
                        routeActive ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                    />
                  </Link>
                )
              }

              if (isOnSeparatePage) {
                const handleNavClick = (e) => {
                  e.preventDefault()
                  navigateToHomeSection(item.href)
                }
                return (
                  <a
                    key={item.key}
                    href={`${marketingHomePath}${item.href.startsWith('#') ? item.href : `#${item.href}`}`}
                    onClick={handleNavClick}
                    className={`group relative whitespace-nowrap px-2.5 py-2.5 text-base font-semibold transition-colors duration-200 ${
                      active ? 'text-primary-darkGreen' : 'text-gray-700 hover:text-primary-darkGreen'
                    }`}
                  >
                    {t(item.key)}
                    <span
                      className={`absolute bottom-0 left-0 h-0.5 bg-primary-darkGreen transition-all duration-300 ${
                        active ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                    />
                  </a>
                )
              }

              return (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className={`group relative whitespace-nowrap px-2.5 py-2.5 text-base font-semibold transition-colors duration-200 ${
                    active ? 'text-primary-darkGreen' : 'text-gray-700 hover:text-primary-darkGreen'
                  }`}
                >
                  {t(item.key)}
                  <span
                    className={`absolute bottom-0 left-0 h-0.5 bg-primary-darkGreen transition-all duration-300 ${
                      active ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </a>
              )
            })}
            </div>
          </nav>

          {/* Contact CTA + language — natural width only */}
          <div ref={langRef} className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
            <Button
              asChild
              size="sm"
              className="hidden bg-primary-darkGreen text-white hover:bg-primary-darkGreen/90 md:inline-flex"
            >
              {isOnSeparatePage ? (
                <Link to={hrefLang('/contact')}>{t('common.buttons.contactUs')}</Link>
              ) : (
                <a href="#contact-form" onClick={(e) => scrollToSection(e, '#contact-form')}>
                  {t('common.buttons.contactUs')}
                </a>
              )}
            </Button>
            <LanguageSwitcher />
          </div>
        </div>

        {/* Mobile Navigation - Simplified with icons */}
        <div
          className={`${isMenuOpen ? 'block' : 'hidden'} ${showDesktopNav ? 'lg:hidden' : ''} max-h-[min(70vh,calc(100dvh-4.5rem))] overflow-y-auto overscroll-contain`}
          id="mobile-menu"
        >
          <div className="space-y-2 border-t border-gray-200 bg-white/95 px-3 pb-[max(1rem,env(safe-area-inset-bottom,0px))] pt-4 backdrop-blur-md">
            {navigation.map((item, index) => {
              if (item.kind === 'external') {
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex touch-manipulation items-center gap-4 rounded-xl px-4 py-4 text-base font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 hover:text-primary-darkGreen active:bg-gray-100"
                  >
                    <span className="flex-shrink-0 text-gray-600">{getIcon(item.icon)}</span>
                    <span className="flex-1">{t(item.key)}</span>
                    <ExternalLinkIcon className="h-4 w-4 shrink-0 text-gray-400 opacity-100" />
                  </a>
                )
              }

              if (item.kind === 'mega') {
                return renderMobileMega(item)
              }

              const isTeamItem = item.sectionId === 'team'
              const active = isOnSeparatePage
                ? isTeamRoutePath && isTeamItem
                : activeSection === item.sectionId

              if (item.isRoute) {
                const to = item.href.startsWith('/') ? item.href : `/${item.href}`
                const routeActive = location.pathname === to || location.pathname.endsWith(to)
                return (
                  <Link
                    key={`${item.key}-${index}`}
                    to={to}
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex touch-manipulation items-center gap-4 rounded-xl px-4 py-4 text-base font-medium transition-colors duration-200 ${
                      routeActive
                        ? 'bg-primary-darkGreen/10 font-semibold text-primary-darkGreen'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-primary-darkGreen active:bg-gray-100'
                    }`}
                  >
                    <span className={`shrink-0 ${routeActive ? 'text-primary-darkGreen' : 'text-gray-600'}`}>
                      {getIcon(item.icon)}
                    </span>
                    <span className="flex-1">{t(item.key)}</span>
                  </Link>
                )
              }

              if (isOnSeparatePage) {
                const itemHref = item.href
                const itemSectionId = item.sectionId
                const sectionLink = `${marketingHomePath}${itemHref.startsWith('#') ? itemHref : `#${itemHref}`}`

                const handleNavClick = (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  navigateToHomeSection(itemHref)
                  setIsMenuOpen(false)
                }
                return (
                  <a
                    key={`${itemSectionId}-${index}`}
                    href={sectionLink}
                    onClick={handleNavClick}
                    data-section-id={itemSectionId}
                    className={`flex touch-manipulation items-center gap-4 rounded-xl px-4 py-4 text-base font-medium transition-colors duration-200 ${
                      active
                        ? 'bg-primary-darkGreen/10 font-semibold text-primary-darkGreen'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-primary-darkGreen active:bg-gray-100'
                    }`}
                  >
                    <span className={`flex-shrink-0 ${active ? 'text-primary-darkGreen' : 'text-gray-600'}`}>
                      {getIcon(item.icon)}
                    </span>
                    <span className="flex-1">{t(item.key)}</span>
                  </a>
                )
              }

              const itemHref = item.href
              const itemSectionId = item.sectionId

              const handleClick = (e) => {
                e.preventDefault()
                e.stopPropagation()
                scrollToSection(e, itemHref)
              }

              return (
                <a
                  key={`${itemSectionId}-${index}`}
                  href={itemHref}
                  onClick={handleClick}
                  data-section-id={itemSectionId}
                  className={`flex touch-manipulation items-center gap-4 rounded-xl px-4 py-4 text-base font-medium transition-colors duration-200 ${
                    active
                      ? 'bg-primary-darkGreen/10 font-semibold text-primary-darkGreen'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-primary-darkGreen active:bg-gray-100'
                  }`}
                >
                  <span className={`flex-shrink-0 ${active ? 'text-primary-darkGreen' : 'text-gray-600'}`}>
                    {getIcon(item.icon)}
                  </span>
                  <span className="flex-1">{t(item.key)}</span>
                </a>
              )
            })}
            <div className="border-t border-gray-200 pt-3 md:hidden">
              <Button asChild className="w-full bg-primary-darkGreen text-white hover:bg-primary-darkGreen/90">
                {isOnSeparatePage ? (
                  <Link to={hrefLang('/contact')} onClick={() => setIsMenuOpen(false)}>
                    {t('common.buttons.contactUs')}
                  </Link>
                ) : (
                  <a
                    href="#contact-form"
                    onClick={(e) => {
                      setIsMenuOpen(false)
                      scrollToSection(e, '#contact-form')
                    }}
                  >
                    {t('common.buttons.contactUs')}
                  </a>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default HeaderSinglePage
